import en from "./en.json";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  isLocale,
  type Locale,
} from "@/lib/i18n/config";

export type ContentDictionary = typeof en;

const cache = new Map<Locale, ContentDictionary>();
cache.set("en", en);

/**
 * One dictionary JSON per locale. English is eager (fallback); every other
 * pack is a separate async chunk so the homepage does not download 42 files.
 */
const DICTIONARY_LOADERS: Record<
  Exclude<Locale, "en">,
  () => Promise<ContentDictionary>
> = {
  es: () => import("./es.json").then((m) => m.default as ContentDictionary),
  fr: () => import("./fr.json").then((m) => m.default as ContentDictionary),
  de: () => import("./de.json").then((m) => m.default as ContentDictionary),
  it: () => import("./it.json").then((m) => m.default as ContentDictionary),
  pt: () => import("./pt.json").then((m) => m.default as ContentDictionary),
  "pt-BR": () =>
    import("./pt-BR.json").then((m) => m.default as ContentDictionary),
  nl: () => import("./nl.json").then((m) => m.default as ContentDictionary),
  pl: () => import("./pl.json").then((m) => m.default as ContentDictionary),
  sv: () => import("./sv.json").then((m) => m.default as ContentDictionary),
  da: () => import("./da.json").then((m) => m.default as ContentDictionary),
  no: () => import("./no.json").then((m) => m.default as ContentDictionary),
  fi: () => import("./fi.json").then((m) => m.default as ContentDictionary),
  cs: () => import("./cs.json").then((m) => m.default as ContentDictionary),
  sk: () => import("./sk.json").then((m) => m.default as ContentDictionary),
  sl: () => import("./sl.json").then((m) => m.default as ContentDictionary),
  hr: () => import("./hr.json").then((m) => m.default as ContentDictionary),
  hu: () => import("./hu.json").then((m) => m.default as ContentDictionary),
  ro: () => import("./ro.json").then((m) => m.default as ContentDictionary),
  el: () => import("./el.json").then((m) => m.default as ContentDictionary),
  ca: () => import("./ca.json").then((m) => m.default as ContentDictionary),
  ru: () => import("./ru.json").then((m) => m.default as ContentDictionary),
  uk: () => import("./uk.json").then((m) => m.default as ContentDictionary),
  tr: () => import("./tr.json").then((m) => m.default as ContentDictionary),
  ar: () => import("./ar.json").then((m) => m.default as ContentDictionary),
  he: () => import("./he.json").then((m) => m.default as ContentDictionary),
  ur: () => import("./ur.json").then((m) => m.default as ContentDictionary),
  hi: () => import("./hi.json").then((m) => m.default as ContentDictionary),
  bn: () => import("./bn.json").then((m) => m.default as ContentDictionary),
  gu: () => import("./gu.json").then((m) => m.default as ContentDictionary),
  mr: () => import("./mr.json").then((m) => m.default as ContentDictionary),
  ta: () => import("./ta.json").then((m) => m.default as ContentDictionary),
  te: () => import("./te.json").then((m) => m.default as ContentDictionary),
  pa: () => import("./pa.json").then((m) => m.default as ContentDictionary),
  ja: () => import("./ja.json").then((m) => m.default as ContentDictionary),
  ko: () => import("./ko.json").then((m) => m.default as ContentDictionary),
  "zh-Hans": () =>
    import("./zh-Hans.json").then((m) => m.default as ContentDictionary),
  "zh-Hant": () =>
    import("./zh-Hant.json").then((m) => m.default as ContentDictionary),
  id: () => import("./id.json").then((m) => m.default as ContentDictionary),
  ms: () => import("./ms.json").then((m) => m.default as ContentDictionary),
  vi: () => import("./vi.json").then((m) => m.default as ContentDictionary),
  th: () => import("./th.json").then((m) => m.default as ContentDictionary),
};

export function getContentDictionary(locale: Locale): ContentDictionary {
  return cache.get(locale) ?? cache.get(DEFAULT_LOCALE) ?? en;
}

export function getContentDictionaryForLocale(input: unknown): ContentDictionary {
  const locale =
    typeof input === "string" && isLocale(input) ? input : DEFAULT_LOCALE;
  return getContentDictionary(locale);
}

export function dictionarySection<K extends keyof ContentDictionary>(
  locale: Locale,
  key: K,
): ContentDictionary[K] {
  const dict = getContentDictionary(locale);
  const value = dict[key];
  if (value !== undefined && value !== null) return value;
  return getContentDictionary(DEFAULT_LOCALE)[key];
}

export async function loadContentDictionary(
  locale: Locale,
): Promise<ContentDictionary> {
  const hit = cache.get(locale);
  if (hit) return hit;
  if (locale === "en") {
    cache.set(locale, en);
    return en;
  }
  const loader = DICTIONARY_LOADERS[locale];
  if (!loader) return en;
  try {
    const dict = await loader();
    cache.set(locale, dict);
    return dict;
  } catch {
    return en;
  }
}

export function prefetchContentDictionary(locale: Locale): void {
  if (locale === DEFAULT_LOCALE || cache.has(locale)) return;
  void loadContentDictionary(locale);
}

export function listedDictionaryLocales(): readonly Locale[] {
  return SUPPORTED_LOCALES;
}
